// Locale-aware `Link`: the plain `next/link` emits a locale-LESS path, which
// costs a middleware redirect round-trip on every click. (`tel:` / `mailto:`
// links on these pages are plain <a>s and are unaffected.)
import { Link } from "@/i18n/routing";
import { ArrowUpRight } from "lucide-react";
import type { ReactNode } from "react";
import { EJINSIGHT_COLORS, EJINSIGHT_TYPE } from "@/lib/theme/ejinsight";
import { Eyebrow } from "./eyebrow";

type SectionHeaderProps = {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  moreLabel?: string;
  moreHref?: string;
  align?: "left" | "between";
  toneOnDark?: boolean;
};

export function SectionHeader({
  eyebrow,
  title,
  description,
  moreLabel = "View all",
  moreHref,
  align = "between",
  toneOnDark = false,
}: SectionHeaderProps) {
  const titleColor = toneOnDark ? EJINSIGHT_COLORS.textOnDark : EJINSIGHT_COLORS.ink;
  const descColor = toneOnDark
    ? EJINSIGHT_COLORS.textOnDarkMuted
    : EJINSIGHT_COLORS.muted;

  return (
    <div
      className={`flex w-full flex-col gap-4 ${
        align === "between" ? "sm:flex-row sm:items-end sm:justify-between" : ""
      }`}
    >
      <div className="flex flex-col gap-3">
        {eyebrow && (
          <Eyebrow tone={toneOnDark ? "onDark" : "primary"} withDash>
            {eyebrow}
          </Eyebrow>
        )}
        <h2
          className="m-0 max-w-[680px]"
          style={{
            ...EJINSIGHT_TYPE.h2,
            color: titleColor,
          }}
        >
          {title}
        </h2>
        {description && (
          <p
            className="m-0 max-w-[640px]"
            style={{ ...EJINSIGHT_TYPE.bodySm, color: descColor }}
          >
            {description}
          </p>
        )}
      </div>
      {moreHref && (
        <Link
          href={moreHref}
          className="group inline-flex shrink-0 items-center gap-1 self-start sm:self-end"
          style={{
            ...EJINSIGHT_TYPE.navLink,
            color: toneOnDark ? EJINSIGHT_COLORS.textOnDark : EJINSIGHT_COLORS.primary,
          }}
        >
          {moreLabel}
          <ArrowUpRight
            className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            strokeWidth={2.4}
          />
        </Link>
      )}
    </div>
  );
}
